import { Box, Button, Card, Circle, HStack, Text, VStack } from "@chakra-ui/react";
import type React from "react";
import { useMemo } from "react";
import { Link } from "react-router";

import { EntityTypeIcon, TEntityType } from "@equidefi/portals/components/EntityTypeIcon";
import { useInvestorDisplayName } from "@equidefi/portals/hooks/useInvestorDisplayName";
import { INDIVIDUAL, TA_ACCOUNT_TYPE } from "@equidefi/shared/constants/profile";
import { blankIfNA } from "@equidefi/shared/helpers/string";
import { Icon } from "@equidefi/ui/icon";

import { CanEdit } from "@/components/permissions/Can";
import type { TVaultDTO } from "@/types/dto";

interface Address {
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface ProfileCardProps {
  vault: TVaultDTO;
  vaultRecords?: TVaultDTO[];
  address?: Address;
  hasAddress?: boolean;
  onEdit?: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ vault, vaultRecords, address, hasAddress, onEdit }) => {
  const displayName = useInvestorDisplayName(vault, vaultRecords);

  const typeLabel = useMemo(() => {
    const type = vault.account_type || vault.entity_type;
    const basic = type ? (TA_ACCOUNT_TYPE[type] ?? type) : "N/A";
    return vault.ta_sub_type && type !== INDIVIDUAL ? `${basic} (${vault.ta_sub_type})` : basic;
  }, [vault]);

  const iconType: TEntityType = vault.account_type || vault.entity_type;

  return (
    <Card bg="white" border="1px solid" borderColor="gray.200" p={5}>
      <HStack spacing={2} align="center" mb="3">
        <EntityTypeIcon type={iconType} size="2.3em" />

        <Box alignSelf="center" py={1}>
          <HStack spacing={4} align="center">
            <Text fontSize="lg" fontWeight="bold" color="gray.900" lineHeight="1" my={1}>
              {displayName}
            </Text>
            {vault.status === "APPROVED" && (
              <Circle size="20px" bg="green.500">
                <Icon.Check size="1em" color="white" />
              </Circle>
            )}
          </HStack>

          <Text color="gray.500" fontSize="md" mt={0} fontWeight="normal" mb="0">
            {typeLabel}
          </Text>
        </Box>
      </HStack>

      {hasAddress && (
        <VStack align="start" spacing={0} mb={2}>
          <Text fontSize="md" color="gray.700" lineHeight="1" my={1} flexWrap="wrap" maxWidth="20em">
            {address?.street ?? ""}
          </Text>
          <Text fontSize="md" color="gray.700" lineHeight="1" my={1} flexWrap="wrap" maxWidth="20em">
            {address?.city ?? ""}
          </Text>
          <Text fontSize="md" color="gray.700" lineHeight="1" my={1} flexWrap="wrap" maxWidth="20em">
            {[address?.state ?? "", blankIfNA(address?.postal_code), address?.country ?? ""].filter(Boolean).join(", ")}
          </Text>
        </VStack>
      )}

      <CanEdit offeringId={vault.offering_id} userRole="owner">
        {vault.status !== "APPROVED" && (
          <Button
            variant="link"
            colorScheme="blue"
            as={Link}
            to={`/vault/${vault.id}/overview`}
            fontSize="md"
            fontWeight="normal"
            alignSelf="flex-start"
            onClick={onEdit}
            mt={2}>
            Edit Profile
          </Button>
        )}
      </CanEdit>
    </Card>
  );
};

export default ProfileCard;
